import { useState, useEffect, useCallback } from "react";
import { X } from "@phosphor-icons/react";

interface FullscreenImageViewerProps {
	isOpen: boolean;
	images: Array<{ src: string; alt: string }>;
	initialIndex: number;
	onClose: () => void;
}

const FullscreenImageViewer = ({
	isOpen,
	images,
	initialIndex,
	onClose
}: FullscreenImageViewerProps) => {
	const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const [touchStart, setTouchStart] = useState<number | null>(null);

	useEffect(() => {
		if (isOpen) setCurrentIndex(initialIndex);
	}, [isOpen, initialIndex]);

	// Keyboard navigation
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft" && currentIndex > 0)
				setCurrentIndex((i) => i - 1);
			if (e.key === "ArrowRight" && currentIndex < images.length - 1)
				setCurrentIndex((i) => i + 1);
		},
		[currentIndex, images.length, onClose]
	);

	useEffect(() => {
		if (!isOpen) return;
		document.addEventListener("keydown", handleKeyDown);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.body.style.overflow = "";
		};
	}, [isOpen, handleKeyDown]);

	if (!isOpen || images.length === 0) return null;

	const handleTouchStart = (e: React.TouchEvent) => {
		setTouchStart(e.touches[0].clientX);
	};

	const handleTouchEnd = (e: React.TouchEvent) => {
		if (touchStart === null) return;
		const diff = touchStart - e.changedTouches[0].clientX;
		if (Math.abs(diff) > 50) {
			if (diff > 0 && currentIndex < images.length - 1)
				setCurrentIndex((i) => i + 1);
			if (diff < 0 && currentIndex > 0) setCurrentIndex((i) => i - 1);
		}
		setTouchStart(null);
	};

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Image viewer"
			className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
			onClick={onClose}
		>
			{/* Constrained dialog — sizes to the image's natural aspect ratio
			    up to 90vw / 85vh. object-contain on the <img> guarantees we
			    never warp the picture. */}
			<div
				className="relative flex flex-col items-center gap-3 max-w-[90vw] max-h-[85vh]"
				onClick={(e) => e.stopPropagation()}
				onTouchStart={handleTouchStart}
				onTouchEnd={handleTouchEnd}
			>
				{/* Close button */}
				<button
					type="button"
					onClick={onClose}
					className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-white shadow-md border border-border flex items-center justify-center hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					aria-label="Close"
				>
					<X size={18} weight="bold" className="text-foreground" />
				</button>

				{/* Image — capped at 90vw / 75vh; AR preserved via object-contain.
				    Reserve ~10vh of vertical space for the counter + dots when
				    multiple images so the box never overflows 85vh. */}
				<img
					src={images[currentIndex]?.src}
					alt={images[currentIndex]?.alt}
					className={[
						"rounded-xl shadow-lg object-contain bg-black",
						"max-w-[90vw]",
						images.length > 1 ? "max-h-[75vh]" : "max-h-[85vh]"
					].join(" ")}
					draggable={false}
				/>

				{/* Counter + dots */}
				{images.length > 1 && (
					<div className="flex flex-col items-center gap-2">
						<span
							className="text-white/80 text-sm"
							aria-label={`Image ${currentIndex + 1} of ${images.length}`}
						>
							{currentIndex + 1} / {images.length}
						</span>
						<div className="flex gap-2">
							{images.map((_, i) => (
								<button
									key={i}
									type="button"
									onClick={() => setCurrentIndex(i)}
									aria-label={`Go to image ${i + 1}`}
									className={[
										"w-2 h-2 rounded-full transition-colors",
										i === currentIndex ? "bg-white" : "bg-white/40"
									].join(" ")}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default FullscreenImageViewer;
